/**
 * Seller Waiting State Component
 * Shown after seller submits counter-proposals and is waiting for buyer response
 */

import React, { useEffect, useState } from 'react';
import {
  AlertCircle,
  CheckCircle2,
  Clock,
  Download,
  FileText,
  Mail,
  MessageSquare,
  Phone,
  Send,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Textarea } from '@/components/ui/textarea';
import { Contract } from '@/types/ContractType';

interface SellerWaitingStateProps {
  contract: Contract;
  contractId: string;
  pendingProposalsCount?: number;
  onSendMessage?: (message: string) => Promise<void>;
}

export const SellerWaitingState: React.FC<SellerWaitingStateProps> = ({
  contract,
  contractId,
  pendingProposalsCount = 0,
  onSendMessage,
}) => {
  const navigate = useNavigate();
  const [message, setMessage] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [daysUntilExpiration, setDaysUntilExpiration] = useState<number | null>(
    null
  );

  // Calculate days until offer expiration
  useEffect(() => {
    const expirationDate = contract?.timeline?.offerGoodUntillDate;
    if (expirationDate) {
      const expiration = new Date(expirationDate);
      const now = new Date();
      const diffTime = expiration.getTime() - now.getTime();
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      setDaysUntilExpiration(diffDays);
    }
  }, [contract]);

  const handleSendMessage = async () => {
    if (!message.trim()) {
      toast.error('Please enter a message');
      return;
    }

    setIsSending(true);
    try {
      if (onSendMessage) {
        await onSendMessage(message);
      }
      toast.success('Message sent to buyer!');
      setMessage('');
    } catch (error) {
      console.error('Error sending message:', error);
      toast.error('Failed to send message');
    } finally {
      setIsSending(false);
    }
  };

  // Get buyer contact info
  const buyerInfo = contract?.buyerInfo;
  // Priority: seller overrides > product database (source of truth) > legacy agentSellerInfo
  const propertyAddress =
    contract?.sellerPropertyInfo?.propertyAddress ||
    contract?.product?.address?.fullAddress ||
    contract?.product?.address?.street ||
    contract?.agentSellerInfo?.propertyAddress ||
    'Property';

  return (
    <div className="container mx-auto p-6 max-w-6xl">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">
          Counter-Proposals Submitted - Awaiting Buyer Response
        </h1>
        <p className="text-gray-600">
          Your counter-proposals for{' '}
          <span className="font-semibold">{propertyAddress}</span> have been
          submitted to the buyer.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left Column - Status & Timeline */}
        <div className="lg:col-span-2 space-y-6">
          {/* Proposal Status Card */}
          <Card className="border-blue-500 bg-gradient-to-br from-blue-50 to-white">
            <CardHeader>
              <CardTitle className="text-xl font-bold text-blue-800 flex items-center gap-2">
                <Send className="h-6 w-6" />
                Negotiation Status
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-start gap-3">
                <div className="bg-green-100 p-2 rounded-full">
                  <CheckCircle2 className="h-5 w-5 text-green-600" />
                </div>
                <div className="flex-1">
                  <p className="font-semibold text-gray-900">
                    Buyer Submitted Offer
                  </p>
                  <p className="text-sm text-gray-600">
                    Initial offer received on{' '}
                    {contract?.createdAt
                      ? new Date(
                          (contract as any).createdAt
                        ).toLocaleDateString('en-US', {
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric',
                        })
                      : 'N/A'}
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <div className="bg-green-100 p-2 rounded-full">
                  <CheckCircle2 className="h-5 w-5 text-green-600" />
                </div>
                <div className="flex-1">
                  <p className="font-semibold text-gray-900">
                    Counter-Proposals Sent
                  </p>
                  <p className="text-sm text-gray-600">
                    You submitted {pendingProposalsCount} counter-proposal
                    {pendingProposalsCount !== 1 ? 's' : ''} to the buyer
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <div className="bg-yellow-100 p-2 rounded-full animate-pulse">
                  <Clock className="h-5 w-5 text-yellow-600" />
                </div>
                <div className="flex-1">
                  <p className="font-semibold text-gray-900">
                    Awaiting Buyer Response
                  </p>
                  <p className="text-sm text-gray-600">
                    The buyer is reviewing your counter-proposals. They can
                    accept, reject, or propose alternative terms. You'll be
                    notified when they respond.
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-3 opacity-50">
                <div className="bg-gray-100 p-2 rounded-full">
                  <MessageSquare className="h-5 w-5 text-gray-400" />
                </div>
                <div className="flex-1">
                  <p className="font-semibold text-gray-500">
                    Agreement & Signatures
                  </p>
                  <p className="text-sm text-gray-500">
                    Once all terms are agreed, both parties will sign to
                    finalize the contract
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Offer Expiration Card */}
          {daysUntilExpiration !== null && (
            <Card
              className={
                daysUntilExpiration <= 2 ? 'border-red-500' : 'border-gray-200'
              }
            >
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Clock
                    className={`h-5 w-5 ${
                      daysUntilExpiration <= 2
                        ? 'text-red-600'
                        : 'text-gray-600'
                    }`}
                  />
                  Original Offer Expiration
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-center">
                  <div
                    className={`text-5xl font-bold mb-2 ${
                      daysUntilExpiration <= 0
                        ? 'text-red-600'
                        : daysUntilExpiration <= 2
                        ? 'text-orange-600'
                        : 'text-blue-600'
                    }`}
                  >
                    {daysUntilExpiration}
                  </div>
                  <p className="text-gray-600">
                    {daysUntilExpiration === 1 ? 'Day' : 'Days'} Remaining
                  </p>
                  <p className="text-sm text-gray-500 mt-2">
                    Offer expires on{' '}
                    {new Date(
                      contract?.timeline?.offerGoodUntillDate || ''
                    ).toLocaleDateString('en-US', {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric',
                    })}
                  </p>
                  {daysUntilExpiration <= 2 && daysUntilExpiration > 0 && (
                    <div className="mt-4 p-3 bg-orange-50 border border-orange-200 rounded-lg">
                      <p className="text-sm text-orange-800 flex items-center gap-2">
                        <AlertCircle className="h-4 w-4" />
                        Offer expiring soon! The buyer may need to submit a new
                        offer if negotiations continue.
                      </p>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Quick Actions */}
          <Card>
            <CardHeader>
              <CardTitle>Quick Actions</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <Button
                variant="outline"
                className="w-full justify-start"
                onClick={() => navigate(`/view-contract/${contractId}`)}
              >
                <FileText className="mr-2 h-4 w-4" />
                View Contract
              </Button>
              <Button
                variant="outline"
                className="w-full justify-start"
                onClick={() => navigate(`/contract/download/${contractId}`)}
              >
                <Download className="mr-2 h-4 w-4" />
                Download PDF Contract
              </Button>
              <Button
                variant="outline"
                className="w-full justify-start"
                onClick={() => navigate('/seller/main')}
              >
                <svg
                  className="mr-2 h-4 w-4"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6"
                  />
                </svg>
                Back to Properties
              </Button>
            </CardContent>
          </Card>
        </div>

        {/* Right Column - Buyer Contact & Messaging */}
        <div className="space-y-6">
          {/* Buyer Contact Card */}
          {buyerInfo && (
            <Card className="border-gray-300">
              <CardHeader>
                <CardTitle className="text-lg">Buyer Contact</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <p className="text-sm text-gray-600">Buyer Name</p>
                  <p className="font-semibold text-gray-900">
                    {buyerInfo.name || 'Not Available'}
                  </p>
                </div>

                {buyerInfo.email && (
                  <div>
                    <p className="text-sm text-gray-600 mb-1">Email</p>
                    <a
                      href={`mailto:${buyerInfo.email}`}
                      className="flex items-center gap-2 text-blue-600 hover:text-blue-800 transition-colors"
                    >
                      <Mail className="h-4 w-4" />
                      <span className="text-sm">{buyerInfo.email}</span>
                    </a>
                  </div>
                )}

                {buyerInfo.phone && (
                  <div>
                    <p className="text-sm text-gray-600 mb-1">Phone</p>
                    <a
                      href={`tel:${buyerInfo.phone}`}
                      className="flex items-center gap-2 text-blue-600 hover:text-blue-800 transition-colors"
                    >
                      <Phone className="h-4 w-4" />
                      <span className="text-sm">{buyerInfo.phone}</span>
                    </a>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Message Buyer Card */}
          <Card className="border-gray-300">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <MessageSquare className="h-5 w-5" />
                Message Buyer
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <label
                  htmlFor="buyer-message"
                  className="block text-sm font-medium text-gray-700 mb-2"
                >
                  Send a message through Unbeatable
                </label>
                <Textarea
                  id="buyer-message"
                  placeholder="e.g., I'm open to discussing the closing date if needed..."
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  rows={5}
                  className="w-full"
                />
              </div>
              <Button
                onClick={handleSendMessage}
                disabled={!message.trim() || isSending}
                className="w-full bg-blue-600 hover:bg-blue-700 text-white"
              >
                {isSending ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Sending...
                  </>
                ) : (
                  <>
                    <Mail className="mr-2 h-4 w-4" />
                    Send Message
                  </>
                )}
              </Button>
              <p className="text-xs text-gray-500 text-center">
                Messages are sent securely through the Unbeatable platform
              </p>
            </CardContent>
          </Card>

          {/* Counter-Proposals Summary Card */}
          <Card className="border-gray-300">
            <CardHeader>
              <CardTitle className="text-lg">Your Counter-Proposals</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div>
                <p className="text-sm text-gray-600">Proposals Submitted</p>
                <p className="text-xl font-bold text-blue-700">
                  {pendingProposalsCount}
                </p>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <p className="text-sm text-gray-600">Original Offer</p>
                  <p className="font-medium text-gray-900">
                    $
                    {contract?.offerSpecifics?.offerPrice?.toLocaleString() ||
                      'N/A'}
                  </p>
                </div>

                <div>
                  <p className="text-sm text-gray-600">Closing Date</p>
                  <p className="font-medium text-gray-900">
                    {contract?.timeline?.closingDate
                      ? new Date(
                          contract.timeline.closingDate
                        ).toLocaleDateString('en-US', {
                          month: 'short',
                          day: 'numeric',
                          year: 'numeric',
                        })
                      : 'N/A'}
                  </p>
                </div>
              </div>

              <div className="pt-3 border-t">
                <p className="text-xs text-gray-500 italic">
                  Once the buyer responds, you'll see their decision here.
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* What Happens Next Section */}
      <Card className="mt-6 border-gray-200">
        <CardHeader>
          <CardTitle>What Happens Next?</CardTitle>
        </CardHeader>
        <CardContent>
          <ol className="space-y-4">
            <li className="flex gap-3">
              <span className="flex-shrink-0 w-8 h-8 bg-green-100 text-green-800 rounded-full flex items-center justify-center font-semibold">
                ✓
              </span>
              <div className="flex-1">
                <p className="font-semibold text-gray-900">
                  You Submitted Counter-Proposals
                </p>
                <p className="text-sm text-gray-600 mt-1">
                  Your {pendingProposalsCount} counter-proposal
                  {pendingProposalsCount !== 1 ? 's' : ''} have been sent to the
                  buyer for review.
                </p>
              </div>
            </li>

            <li className="flex gap-3">
              <span className="flex-shrink-0 w-8 h-8 bg-yellow-100 text-yellow-800 rounded-full flex items-center justify-center font-semibold animate-pulse">
                2
              </span>
              <div className="flex-1">
                <p className="font-semibold text-gray-900">
                  Buyer Reviews Your Proposals
                </p>
                <p className="text-sm text-gray-600 mt-1">
                  The buyer will review each of your counter-proposals and
                  decide whether to accept, reject, or propose alternative
                  terms.
                </p>
              </div>
            </li>

            <li className="flex gap-3 opacity-50">
              <span className="flex-shrink-0 w-8 h-8 bg-gray-100 text-gray-500 rounded-full flex items-center justify-center font-semibold">
                3
              </span>
              <div className="flex-1">
                <p className="font-semibold text-gray-500">
                  Negotiation Continues
                </p>
                <p className="text-sm text-gray-500 mt-1">
                  If the buyer proposes changes, you'll see them on this page
                  and can accept, reject, or counter again. You'll receive a
                  notification when their response is ready.
                </p>
              </div>
            </li>

            <li className="flex gap-3 opacity-50">
              <span className="flex-shrink-0 w-8 h-8 bg-gray-100 text-gray-500 rounded-full flex items-center justify-center font-semibold">
                4
              </span>
              <div className="flex-1">
                <p className="font-semibold text-gray-500">
                  Agreement & Signatures
                </p>
                <p className="text-sm text-gray-500 mt-1">
                  Once all terms are agreed, both parties will sign the contract
                  to make it legally binding.
                </p>
              </div>
            </li>
          </ol>
        </CardContent>
      </Card>

      {/* Tips for Sellers */}
      <Card className="mt-6 border-blue-200 bg-blue-50">
        <CardHeader>
          <CardTitle className="text-lg text-blue-900">
            💡 Seller Tips
          </CardTitle>
        </CardHeader>
        <CardContent>
          <ul className="space-y-2 text-sm text-blue-800">
            <li className="flex items-start gap-2">
              <span>•</span>
              <span>Buyers typically respond within 24-48 hours</span>
            </li>
            <li className="flex items-start gap-2">
              <span>•</span>
              <span>
                You can message the buyer to clarify any terms or discuss
                concerns
              </span>
            </li>
            <li className="flex items-start gap-2">
              <span>•</span>
              <span>
                Consider your priorities - be flexible on less important terms
              </span>
            </li>
            <li className="flex items-start gap-2">
              <span>•</span>
              <span>
                You'll be notified immediately when the buyer responds
              </span>
            </li>
          </ul>
        </CardContent>
      </Card>
    </div>
  );
};

export default SellerWaitingState;
